import React, { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { apiFetch } from "../utils/apiFetch"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

const pageStyle = {
  minHeight: "100vh",
  background: "#f8fafc",
  padding: "24px 20px 40px",
}

const shellStyle = {
  maxWidth: 980,
  margin: "0 auto",
  display: "grid",
  gap: 14,
}

const cardStyle = {
  border: "1px solid #e2e8f0",
  background: "#ffffff",
  borderRadius: 18,
  padding: 16,
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
}

const heroStyle = {
  borderRadius: 22,
  padding: 18,
  background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 56%, #7c3aed 100%)",
  color: "#ffffff",
  display: "grid",
  gap: 8,
  boxShadow: "0 18px 34px rgba(15,23,42,0.14)",
}

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  width: "fit-content",
  padding: "6px 10px",
  borderRadius: 999,
  background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.18)",
  fontSize: 11,
  fontWeight: 900,
  letterSpacing: 0.4,
}

const sectionTitleStyle = {
  fontSize: 18,
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: 6,
  lineHeight: 1.15,
}

const sectionSubtitleStyle = {
  fontSize: 13,
  color: "#64748b",
  marginBottom: 14,
  lineHeight: 1.45,
}

const inputStyle = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  outline: "none",
  background: "#fff",
  fontSize: 14,
  color: "#0f172a",
}

const textareaStyle = {
  ...inputStyle,
  resize: "vertical",
  minHeight: 96,
}

const primaryButtonStyle = {
  padding: "11px 16px",
  borderRadius: 12,
  border: "1px solid #0f172a",
  background: "#0f172a",
  color: "#ffffff",
  cursor: "pointer",
  fontWeight: 800,
}

const secondaryButtonStyle = {
  padding: "11px 16px",
  borderRadius: 12,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#0f172a",
  cursor: "pointer",
  fontWeight: 800,
}

const successButtonStyle = {
  ...primaryButtonStyle,
  background: "#166534",
  border: "1px solid #166534",
}

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : ""
}

function normalizeDateInput(value) {
  if (!value) return ""
  return String(value).slice(0, 10)
}

function pickRicherName(currentValue, incomingValue) {
  const current = normalizeText(currentValue)
  const incoming = normalizeText(incomingValue)

  if (!current) return incoming
  if (!incoming) return current

  const currentWords = current.split(/\s+/).filter(Boolean).length
  const incomingWords = incoming.split(/\s+/).filter(Boolean).length

  if (currentWords !== incomingWords) {
    return incomingWords > currentWords ? incoming : current
  }

  return incoming.length > current.length ? incoming : current
}

function mergeFormWithProfileSeed(currentForm, seed) {
  if (!seed) return currentForm

  return {
    ...currentForm,
    firstName: pickRicherName(currentForm.firstName, seed.firstName),
    lastName: pickRicherName(currentForm.lastName, seed.lastName),
    phone: normalizeText(currentForm.phone) || normalizeText(seed.phone) || "",
    email: normalizeText(currentForm.email) || normalizeText(seed.email) || "",
    birthDate: currentForm.birthDate || normalizeDateInput(seed.birthDate),
    address: normalizeText(currentForm.address) || normalizeText(seed.address) || "",
    occupation: normalizeText(currentForm.occupation) || normalizeText(seed.occupation) || "",
    education: normalizeText(currentForm.education) || normalizeText(seed.education) || "",
    maritalStatus:
      normalizeText(currentForm.maritalStatus) ||
      normalizeText(seed.maritalStatus) ||
      "",
    gender: normalizeText(currentForm.gender) || normalizeText(seed.gender) || "",
    recommended:
      normalizeText(currentForm.recommended) ||
      normalizeText(seed.recommended) ||
      "",
    recommendedBy:
      normalizeText(currentForm.recommendedBy) ||
      normalizeText(seed.recommendedBy) ||
      "",
    lastDentalVisitDate:
      currentForm.lastDentalVisitDate || normalizeDateInput(seed.lastDentalVisitDate),
    lastDentalVisitReason:
      normalizeText(currentForm.lastDentalVisitReason) ||
      normalizeText(seed.lastDentalVisitReason) ||
      "",
    habits:
      Array.isArray(currentForm.habits) && currentForm.habits.length
        ? currentForm.habits
        : Array.isArray(seed.habits)
          ? seed.habits
          : [],
    habitsDuration:
      normalizeText(currentForm.habitsDuration) ||
      normalizeText(seed.habitsDuration) ||
      "",
    medicalConditions:
      Array.isArray(currentForm.medicalConditions) && currentForm.medicalConditions.length
        ? currentForm.medicalConditions
        : Array.isArray(seed.medicalConditions)
          ? seed.medicalConditions
          : [],
    medications:
      normalizeText(currentForm.medications) ||
      normalizeText(seed.medications) ||
      "",
    allergiesDetail:
      normalizeText(currentForm.allergiesDetail) ||
      normalizeText(seed.allergiesDetail) ||
      "",
    medicalNotes:
      normalizeText(currentForm.medicalNotes) ||
      normalizeText(seed.medicalNotes) ||
      "",
    observations:
      normalizeText(currentForm.observations) ||
      normalizeText(seed.observations) ||
      "",
  }
}

function getTodayDateInput() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function Field({ label, children }) {
  return (
    <div>
      <label
        style={{
          display: "block",
          marginBottom: 6,
          fontSize: 13,
          color: "#475569",
          fontWeight: 700,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  )
}

function CheckboxGroup({ options, values, onChange, columns = 2, disabled = false }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: 10,
      }}
    >
      {options.map((option) => (
        <label
          key={option.value}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            color: "#334155",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.75 : 1,
          }}
        >
          <input
            type="checkbox"
            checked={values.includes(option.value)}
            disabled={disabled}
            onChange={(e) => {
              if (e.target.checked) {
                onChange([...values, option.value])
              } else {
                onChange(values.filter((item) => item !== option.value))
              }
            }}
          />
          {option.label}
        </label>
      ))}
    </div>
  )
}

function RadioGroup({ name, options, value, onChange, disabled = false }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
      {options.map((option) => (
        <label
          key={option.value}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
            color: "#334155",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.75 : 1,
          }}
        >
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  )
}

function formatDate(value) {
  if (!value) return "â€”"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "â€”"
  return d.toLocaleDateString("es-MX")
}

function formatDateTime(value) {
  if (!value) return "â€”"
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return "â€”"
  return d.toLocaleString("es-MX", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function InfoChip({ children, tone = "neutral" }) {
  const visual =
    tone === "success"
      ? { bg: "#f0fdf4", border: "#bbf7d0", color: "#166534" }
      : tone === "info"
        ? { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" }
        : { bg: "#ffffff", border: "#e2e8f0", color: "#334155" }

  return (
    <div
      style={{
        padding: "7px 10px",
        borderRadius: 999,
        border: `1px solid ${visual.border}`,
        background: visual.bg,
        color: visual.color,
        fontSize: 12,
        fontWeight: 800,
      }}
    >
      {children}
    </div>
  )
}

export default function PublicClinicalIntakePage() {
  const { token } = useParams()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [meta, setMeta] = useState(null)
  const [submitOk, setSubmitOk] = useState(false)

  const [checkingExisting, setCheckingExisting] = useState(false)
  const [existingLookupReady, setExistingLookupReady] = useState(false)
  const [existingPatientInfo, setExistingPatientInfo] = useState(null)
  const [existingPrefillPatientId, setExistingPrefillPatientId] = useState("")

  const [form, setForm] = useState({
    date: getTodayDateInput(),
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    birthDate: "",
    address: "",
    occupation: "",
    education: "",
    maritalStatus: "",
    gender: "",
    recommended: "",
    recommendedBy: "",
    chiefComplaintTags: [],
    chiefComplaintOther: "",
    chiefComplaintDescription: "",
    painLevel: "",
    symptomSince: "",
    lastDentalVisitDate: "",
    lastDentalVisitReason: "",
    habits: [],
    habitsDuration: "",
    medicalConditions: [],
    medications: "",
    allergiesDetail: "",
    medicalNotes: "",
    observations: "",
    consentAccepted: false,
  })

  const chiefComplaintOptions = [
    { value: "PAIN", label: "Dolor dental" },
    { value: "CARIES", label: "Caries" },
    { value: "GINGIVITIS", label: "Gingivitis" },
    { value: "ORTHODONTICS", label: "Ortodoncia" },
    { value: "ESTHETICS", label: "EstÃ©tica" },
    { value: "CLEANING", label: "Limpieza" },
    { value: "FRACTURE", label: "Fractura dental" },
    { value: "EXTRACTION", label: "ExtracciÃ³n dental" },
    { value: "OTHER", label: "Otro" },
  ]

  const habitOptions = [
    { value: "BRUXISM", label: "Bruxismo" },
    { value: "MOUTH_BREATHING", label: "RespiraciÃ³n bucal" },
    { value: "NAIL_BITING", label: "Onicofagia" },
    { value: "BITE_OBJECTS", label: "Morder objetos" },
    { value: "OTHER", label: "Otro" },
  ]

  const medicalOptions = [
    { value: "DIABETES", label: "Diabetes" },
    { value: "HYPERTENSION", label: "HipertensiÃ³n" },
    { value: "CARDIOPATHY", label: "CardiopatÃ­a" },
    { value: "ALLERGIES", label: "Alergias" },
    { value: "SYSTEMIC_DISEASE", label: "Enfermedad sistÃ©mica" },
    { value: "PREGNANCY", label: "Embarazo" },
    { value: "MEDICATION", label: "Toma medicamento" },
  ]

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleApplyExistingProfileSeed = () => {
    const profileSeed = existingPatientInfo?.profileSeed || null

    if (!profileSeed) {
      alert("No hay datos previos para aplicar.")
      return
    }

    setForm((prev) => mergeFormWithProfileSeed(prev, profileSeed))
    alert("Se aplicaron tus datos previos.")
  }

  const handleConfirmExistingProfileAndContinue = () => {
    const profileSeed = existingPatientInfo?.profileSeed || null

    if (profileSeed) {
      setForm((prev) => mergeFormWithProfileSeed(prev, profileSeed))
    }

    const node = document.getElementById("public-clinical-form-start")
    if (node) {
      node.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)

        const res = await apiFetch(`${API_BASE}/clinical-history/public/intake/${token}`)
        const data = await res.json().catch(() => null)

        if (!res.ok || !data) {
          setMeta({
            error: data?.message || "No se pudo abrir el formulario.",
          })
          return
        }

        setMeta(data)
      } catch {
        setMeta({
          error: "No se pudo abrir el formulario.",
        })
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      load()
    }
  }, [token])

  const canCheckExisting = useMemo(() => {
    return Boolean(
      form.firstName.trim() &&
        form.lastName.trim() &&
        (form.phone.trim() || form.email.trim())
    )
  }, [form.firstName, form.lastName, form.phone, form.email])

  useEffect(() => {
    if (!token) return

    if (!canCheckExisting) {
      setExistingPatientInfo(null)
      setExistingLookupReady(false)
      setExistingPrefillPatientId("")
      return
    }

    const timeout = setTimeout(async () => {
      try {
        setCheckingExisting(true)

        const res = await apiFetch(
          `${API_BASE}/clinical-history/public/intake/${token}/check-patient`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              firstName: form.firstName,
              lastName: form.lastName,
              phone: form.phone,
              email: form.email,
            }),
          }
        )

        const data = await res.json().catch(() => null)

        if (!res.ok || !data) {
          setExistingPatientInfo(null)
          setExistingLookupReady(false)
          return
        }

        setExistingPatientInfo(data)
        setExistingLookupReady(Boolean(data.ready))
      } catch {
        setExistingPatientInfo(null)
        setExistingLookupReady(false)
      } finally {
        setCheckingExisting(false)
      }
    }, 450)

    return () => clearTimeout(timeout)
  }, [token, canCheckExisting, form.firstName, form.lastName, form.phone, form.email])

  useEffect(() => {
    const patientId = existingPatientInfo?.patient?.id || ""
    const profileSeed = existingPatientInfo?.profileSeed || null

    if (!existingPatientInfo?.found || !patientId || !profileSeed) {
      return
    }

    if (existingPrefillPatientId === patientId) {
      return
    }

    setForm((prev) => mergeFormWithProfileSeed(prev, profileSeed))
    setExistingPrefillPatientId(patientId)
  }, [existingPatientInfo, existingPrefillPatientId])

  const title = useMemo(() => {
    return meta?.intake?.title || "Pre-registro clÃ­nico"
  }, [meta])

  const clinicName = useMemo(() => {
    return meta?.clinic?.name || "ClÃ­nica"
  }, [meta])

  const welcomeCard = useMemo(() => {
    if (!existingPatientInfo?.found) return null

    const patient = existingPatientInfo.patient || {}
    const summary = existingPatientInfo.summary || {}
    const hasUpcomingAppointment = Boolean(summary.upcomingAppointmentAt)

    return {
      name: `${patient.firstName || ""} ${patient.lastName || ""}`.trim(),
      confidence: existingPatientInfo.matchConfidence || "HIGH",
      latestClinicalHistoryAt: summary.latestClinicalHistoryAt || null,
      latestChiefComplaint: summary.latestChiefComplaint || "",
      upcomingAppointmentAt: summary.upcomingAppointmentAt || null,
      upcomingAppointmentStatus: summary.upcomingAppointmentStatus || "",
      hasUpcomingAppointment,
      appointmentLabel: hasUpcomingAppointment
        ? formatDateTime(summary.upcomingAppointmentAt)
        : "",
    }
  }, [existingPatientInfo])

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (!form.firstName.trim()) {
        alert("Escribe tu nombre.")
        return
      }

      if (!form.lastName.trim()) {
        alert("Escribe tu apellido.")
        return
      }

      if (!form.consentAccepted) {
        alert("Debes confirmar la autorizaciÃ³n de datos.")
        return
      }

      setSubmitting(true)

      const res = await apiFetch(`${API_BASE}/clinical-history/public/intake/${token}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        alert(data?.message || "No se pudo enviar el pre-registro.")
        return
      }

      setSubmitOk(true)
    } catch {
      alert("Error al enviar el pre-registro.")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={shellStyle}>
          <div style={{ ...cardStyle, color: "#64748b", fontWeight: 700 }}>
            Cargando formulario...
          </div>
        </div>
      </div>
    )
  }

  if (meta?.error) {
    return (
      <div style={pageStyle}>
        <div style={shellStyle}>
          <div style={cardStyle}>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a" }}>
              Enlace no disponible
            </div>
            <div style={{ marginTop: 10, color: "#64748b", lineHeight: 1.5 }}>
              {meta.error}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (submitOk) {
    return (
      <div style={pageStyle}>
        <div style={shellStyle}>
          <div style={{ ...cardStyle, maxWidth: 760, margin: "0 auto" }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>
              Â¡Gracias!
            </div>
            <div style={{ marginTop: 10, color: "#475569", lineHeight: 1.6 }}>
              Tu pre-registro clÃ­nico fue enviado correctamente a{" "}
              <strong>{clinicName}</strong>. Ya puedes cerrar esta pantalla.
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={pageStyle}>
      <div style={shellStyle}>
        <div style={heroStyle}>
          <div style={badgeStyle}>{clinicName}</div>

          <div
            style={{
              fontSize: 24,
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
            }}
          >
            {title}
          </div>

          <div
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.92)",
              maxWidth: 760,
            }}
          >
            Completa este formulario antes de tu consulta para agilizar tu atenciÃ³n
            y reducir tiempos de captura en recepciÃ³n.
          </div>
        </div>

        {checkingExisting ? (
          <div
            style={{
              ...cardStyle,
              color: "#64748b",
              fontWeight: 700,
            }}
          >
            Revisando si ya existe un registro previo...
          </div>
        ) : null}

        {welcomeCard ? (
          <div
            style={{
              ...cardStyle,
              border: "1px solid #bbf7d0",
              background: "linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%)",
              padding: 14,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) auto",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: "#166534",
                    lineHeight: 1.15,
                  }}
                >
                  Bienvenido de nuevo, {welcomeCard.name || "paciente"}
                </div>

                <div
                  style={{
                    marginTop: 6,
                    color: "#166534",
                    lineHeight: 1.55,
                    fontSize: 13.5,
                  }}
                >
                  {welcomeCard.hasUpcomingAppointment ? (
                    <>
                      Ya encontramos tu registro previo y ademÃ¡s tienes una cita prÃ³xima.
                      Solo confirma o actualiza tus datos antes de enviarlos.
                    </>
                  ) : welcomeCard.confidence === "MEDIUM" ? (
                    <>
                      Ya encontramos un registro probable en la clÃ­nica. Verifica que tus
                      datos sean correctos antes de enviarlos.
                    </>
                  ) : (
                    <>
                      Ya encontramos tu registro previo en la clÃ­nica. Solo confirma o
                      actualiza tus datos y envÃ­a el formulario.
                    </>
                  )}
                </div>
              </div>

              <InfoChip tone="success">Registro previo encontrado</InfoChip>
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 12,
              }}
            >
              <InfoChip tone="success">
                Ãšltima historia: {formatDate(welcomeCard.latestClinicalHistoryAt)}
              </InfoChip>

              {welcomeCard.upcomingAppointmentAt ? (
                <InfoChip tone="success">
                  PrÃ³xima cita: {welcomeCard.appointmentLabel}
                </InfoChip>
              ) : null}

              {welcomeCard.upcomingAppointmentStatus ? (
                <InfoChip tone="info">
                  Estado cita: {welcomeCard.upcomingAppointmentStatus}
                </InfoChip>
              ) : null}
            </div>

            {welcomeCard.latestChiefComplaint ? (
              <div
                style={{
                  marginTop: 10,
                  color: "#166534",
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                Ãšltimo motivo registrado: {welcomeCard.latestChiefComplaint}
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 12,
              }}
            >
              <button
                type="button"
                onClick={handleApplyExistingProfileSeed}
                style={successButtonStyle}
              >
                Usar mis datos previos
              </button>

              <button
                type="button"
                onClick={handleConfirmExistingProfileAndContinue}
                style={secondaryButtonStyle}
              >
                Confirmar y continuar
              </button>
            </div>
          </div>
        ) : null}

        {existingLookupReady && !existingPatientInfo?.found ? (
          <div
            style={{
              ...cardStyle,
              border: "1px solid #dbeafe",
              background: "#eff6ff",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 900, color: "#1d4ed8" }}>
              No encontramos un registro previo
            </div>
            <div style={{ marginTop: 8, color: "#334155", lineHeight: 1.5 }}>
              ContinÃºa con el formulario y te registraremos como paciente nuevo en
              la clÃ­nica.
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
          <div id="public-clinical-form-start" style={cardStyle}>
            <div style={sectionTitleStyle}>1. Datos generales</div>
            <div style={sectionSubtitleStyle}>
              InformaciÃ³n bÃ¡sica para identificarte correctamente y agilizar tu
              atenciÃ³n.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}
            >
              <Field label="Fecha">
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Nombre(s)">
                <input
                  value={form.firstName}
                  onChange={(e) => updateField("firstName", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Apellido(s)">
                <input
                  value={form.lastName}
                  onChange={(e) => updateField("lastName", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="TelÃ©fono">
                <input
                  value={form.phone}
                  onChange={(e) => updateField("phone", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Correo">
                <input
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Fecha de nacimiento">
                <input
                  type="date"
                  value={form.birthDate}
                  onChange={(e) => updateField("birthDate", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="OcupaciÃ³n">
                <input
                  value={form.occupation}
                  onChange={(e) => updateField("occupation", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Escolaridad">
                <input
                  value={form.education}
                  onChange={(e) => updateField("education", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="DirecciÃ³n">
                  <input
                    value={form.address}
                    onChange={(e) => updateField("address", e.target.value)}
                    style={inputStyle}
                  />
                </Field>
              </div>

              <Field label="Estado civil">
                <input
                  value={form.maritalStatus}
                  onChange={(e) => updateField("maritalStatus", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="GÃ©nero">
                <select
                  value={form.gender}
                  onChange={(e) => updateField("gender", e.target.value)}
                  style={inputStyle}
                >
                  <option value="">Selecciona</option>
                  <option value="FEMALE">Femenino</option>
                  <option value="MALE">Masculino</option>
                  <option value="OTHER">Otro</option>
                </select>
              </Field>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={sectionTitleStyle}>2. Motivo de consulta</div>
            <div style={sectionSubtitleStyle}>
              CuÃ©ntanos quÃ© problema te trae hoy y desde cuÃ¡ndo comenzÃ³.
            </div>

            <Field label="Selecciona uno o varios motivos">
              <CheckboxGroup
                options={chiefComplaintOptions}
                values={form.chiefComplaintTags}
                onChange={(value) => updateField("chiefComplaintTags", value)}
                columns={3}
              />
            </Field>

            {form.chiefComplaintTags.includes("OTHER") ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Otro motivo">
                  <input
                    value={form.chiefComplaintOther}
                    onChange={(e) => updateField("chiefComplaintOther", e.target.value)}
                    style={inputStyle}
                  />
                </Field>
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 2fr) repeat(2, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="Describe tu molestia">
                <textarea
                  value={form.chiefComplaintDescription}
                  onChange={(e) =>
                    updateField("chiefComplaintDescription", e.target.value)
                  }
                  style={textareaStyle}
                />
              </Field>

              <Field label="Nivel de dolor (0-10)">
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={form.painLevel}
                  onChange={(e) => updateField("painLevel", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Â¿Desde cuÃ¡ndo?">
                <input
                  value={form.symptomSince}
                  onChange={(e) => updateField("symptomSince", e.target.value)}
                  placeholder="Ej. 3 dÃ­as, 2 semanas, 1 mes"
                  style={inputStyle}
                />
              </Field>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={sectionTitleStyle}>3. Antecedentes y hÃ¡bitos</div>
            <div style={sectionSubtitleStyle}>
              InformaciÃ³n dental previa y hÃ¡bitos que pueden influir en tu
              tratamiento.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px, 1fr) minmax(0, 2fr)",
                gap: 12,
              }}
            >
              <Field label="Ãšltima consulta dental">
                <input
                  type="date"
                  value={form.lastDentalVisitDate}
                  onChange={(e) => updateField("lastDentalVisitDate", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Motivo de esa consulta">
                <input
                  value={form.lastDentalVisitReason}
                  onChange={(e) =>
                    updateField("lastDentalVisitReason", e.target.value)
                  }
                  style={inputStyle}
                />
              </Field>
            </div>

            <div style={{ marginTop: 14 }}>
              <Field label="HÃ¡bitos">
                <CheckboxGroup
                  options={habitOptions}
                  values={form.habits}
                  onChange={(value) => updateField("habits", value)}
                  columns={3}
                />
              </Field>
            </div>

            {!!form.habits.length ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Detalle del hÃ¡bito">
                  <input
                    value={form.habitsDuration}
                    onChange={(e) => updateField("habitsDuration", e.target.value)}
                    style={inputStyle}
                  />
                </Field>
              </div>
            ) : null}
          </div>

          <div style={cardStyle}>
            <div style={sectionTitleStyle}>4. Antecedentes mÃ©dicos</div>
            <div style={sectionSubtitleStyle}>
              Selecciona los antecedentes relevantes y agrega notas cuando
              aplique.
            </div>

            <Field label="Selecciona antecedentes relevantes">
              <CheckboxGroup
                options={medicalOptions}
                values={form.medicalConditions}
                onChange={(value) => updateField("medicalConditions", value)}
                columns={3}
              />
            </Field>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginTop: 12,
              }}
            >
              <Field label="Medicamentos">
                <input
                  value={form.medications}
                  onChange={(e) => updateField("medications", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Alergias">
                <input
                  value={form.allergiesDetail}
                  onChange={(e) => updateField("allergiesDetail", e.target.value)}
                  style={inputStyle}
                />
              </Field>

              <Field label="Notas mÃ©dicas">
                <input
                  value={form.medicalNotes}
                  onChange={(e) => updateField("medicalNotes", e.target.value)}
                  style={inputStyle}
                />
              </Field>
            </div>
          </div>

          <div style={cardStyle}>
            <div style={sectionTitleStyle}>5. RecomendaciÃ³n y observaciones</div>
            <div style={sectionSubtitleStyle}>
              InformaciÃ³n adicional Ãºtil para recepciÃ³n y seguimiento clÃ­nico.
            </div>

            <Field label="Â¿Alguien te recomendÃ³?">
              <RadioGroup
                name="recommended"
                value={form.recommended}
                onChange={(value) => updateField("recommended", value)}
                options={[
                  { value: "YES", label: "SÃ­" },
                  { value: "NO", label: "No" },
                ]}
              />
            </Field>

            {form.recommended === "YES" ? (
              <div style={{ marginTop: 12 }}>
                <Field label="Â¿QuiÃ©n te recomendÃ³?">
                  <input
                    value={form.recommendedBy}
                    onChange={(e) => updateField("recommendedBy", e.target.value)}
                    style={inputStyle}
                  />
                </Field>
              </div>
            ) : null}

            <div style={{ marginTop: 12 }}>
              <Field label="Observaciones adicionales">
                <textarea
                  value={form.observations}
                  onChange={(e) => updateField("observations", e.target.value)}
                  style={textareaStyle}
                />
              </Field>
            </div>
          </div>

          <div style={cardStyle}>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: 14,
                color: "#334155",
                lineHeight: 1.55,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.consentAccepted}
                onChange={(e) => updateField("consentAccepted", e.target.checked)}
                style={{ marginTop: 3 }}
              />
              Confirmo que la informaciÃ³n proporcionada es correcta y autorizo su
              uso para fines clÃ­nicos y administrativos relacionados con mi atenciÃ³n.
            </label>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                ...primaryButtonStyle,
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.75 : 1,
              }}
            >
              {submitting ? "Enviando..." : "Enviar pre-registro"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
